/**
 * YMM Selector Web Component
 * Handles equipment selection with make, model and serial number.
 *
 * Dimensions follow the YMM mapping contract resolved in KNEK-246: `make` and
 * `model` only. Year was dropped, and `model_number` (serial bucket) stays out of
 * the identity until KNEK-244 defines its buckets, so serial is captured but optional.
 */

if (!customElements.get("ymm-selector")) {
  class YMMSelector extends HTMLElement {
    constructor() {
      super();

      this.state = {
        make: null,
        model: null,
        serial: "",
        equipment: null,
      };

      this.makes = [];
      this.models = {};
      this._initialized = false;
      this._abortController = null;
      this._timers = new Set();
      this._resizeTimer = null;
      this._saveTooltipTimer = null;
      this._dataPromise = null;
    }

    get isDesktopView() {
      return window.innerWidth >= 960;
    }

    connectedCallback() {
      this._abortController = new AbortController();

      if (!this._initialized) {
        this._initialized = true;
        this.init();
      } else {
        this.bindEvents();
      }
    }

    disconnectedCallback() {
      this._abortController?.abort();
      this._abortController = null;
      this._timers.forEach((id) => window.clearTimeout(id));
      this._timers.clear();
    }

    setTimeout(fn, ms) {
      const id = window.setTimeout(() => {
        this._timers.delete(id);
        fn();
      }, ms);
      this._timers.add(id);
      return id;
    }

    clearTimeout(id) {
      window.clearTimeout(id);
      this._timers.delete(id);
    }

    init() {
      this.cacheElements();
      this.bindEvents();
      this.setInitialState();
      this.captureCopy();
      this.applyResponsiveCopy();
    }

    cacheElements() {
      this.container = this.querySelector(".js-ymm-container");
      this.header = this.querySelector(".js-ymm-header");
      this.subtitle = this.querySelector(".js-ymm-subtitle");
      this.toggle = this.querySelector(".js-ymm-toggle");
      this.collapsed = this.querySelector(".js-ymm-collapsed");
      this.expandBtn = this.querySelector(".js-ymm-expand");
      this.form = this.querySelector(".js-ymm-form");
      this.equipment = this.querySelector(".js-ymm-equipment");

      this.makeDropdown = this.querySelector('[data-field="make"]');
      this.modelDropdown = this.querySelector('[data-field="model"]');
      this.modelTrigger = this.modelDropdown?.querySelector(".js-ymm-dropdown-trigger");
      this.serialField = this.querySelector('[data-field="serial"]');
      this.submitBtn = this.querySelector(".js-ymm-submit");
      this.clearSelectionField = this.querySelector(".js-ymm-clear-field");
      this.clearSelectionBtn = this.querySelector(".js-ymm-clear");

      this.equipmentField = this.querySelector('[data-field="equipment"]');
      this.clearBtn = this.querySelector(".js-ymm-equipment-clear");
      this.shopBtn = this.querySelector(".js-ymm-shop");
      this.saveBtn = this.querySelector(".js-ymm-save");
      this.saveTooltip = this.querySelector(".js-ymm-save-tooltip");
    }

    bindEvents() {
      const opts = { signal: this._abortController.signal };

      this.expandBtn?.addEventListener("click", () => this.expand(), opts);
      this.toggle?.addEventListener("click", () => this.toggleExpand(), opts);

      [
        [this.makeDropdown, (option) => this.selectMake(option)],
        [this.modelDropdown, (option) => this.selectModel(option)],
      ].forEach(([dropdown, select]) => {
        if (!dropdown) return;

        dropdown.querySelector(".js-ymm-dropdown-trigger")?.addEventListener(
          "click",
          (e) => {
            if (!dropdown.classList.contains("ymm-dropdown--disabled")) this.toggleDropdown(dropdown, e);
          },
          opts,
        );

        // Delegated to the dropdown root so the handler survives re-rendering the options.
        dropdown.addEventListener(
          "click",
          (e) => {
            const option = e.target.closest(".js-ymm-dropdown-option");
            if (option && dropdown.contains(option)) select(option);
          },
          opts,
        );
      });

      this.serialField?.querySelector(".js-ymm-textfield-input")?.addEventListener(
        "input",
        (e) => {
          this.handleSerialInput(e);
        },
        opts,
      );

      this.submitBtn?.addEventListener("click", () => this.handleSubmit(), opts);
      this.clearSelectionBtn?.addEventListener("click", () => this.handleClearSelection(), opts);
      this.clearBtn?.addEventListener("click", () => this.clearEquipment(), opts);
      this.shopBtn?.addEventListener("click", () => this.shopEquipment(), opts);
      this.saveBtn?.addEventListener("click", () => this.saveToGarage(), opts);

      document.addEventListener("click", (e) => this.handleOutsideClick(e), opts);
      document.addEventListener("keydown", (e) => this.handleKeydown(e), opts);

      window.addEventListener("garage:open-fitment", () => this.handleGarageOpenFitment(), opts);
      window.addEventListener("garage:active-changed", (e) => this.handleGarageActiveChanged(e.detail), opts);
      window.addEventListener("ymm:set-from-product", (e) => this.handleSetFromProduct(e.detail), opts);
      window.addEventListener("resize", () => this.handleResize(), opts);
    }

    handleGarageOpenFitment() {
      this.resetFormForNewEntry();
      this.expand();

      requestAnimationFrame(() => {
        const header =
          document.querySelector(".header") ||
          document.querySelector("header") ||
          document.querySelector(".shopify-section-header-sticky");
        const headerOffset = header ? header.getBoundingClientRect().height : 120;
        const elementPosition = this.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset - 20;

        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth",
        });

        const makeTrigger = this.makeDropdown?.querySelector(".js-ymm-dropdown-trigger");
        if (makeTrigger && !makeTrigger.disabled) {
          makeTrigger.focus({ preventScroll: true });

          this.setTimeout(() => {
            const clickEvent = new MouseEvent("click", {
              view: window,
              bubbles: true,
              cancelable: true,
            });
            makeTrigger.dispatchEvent(clickEvent);
          }, 300);
        }
      });
    }

    // The active unit of the garage drives what the selector shows, so the header
    // dropdown and the on-page selector never disagree about the current equipment.
    handleGarageActiveChanged({ equipment }) {
      // Only follow the garage when a unit BECOMES active. Clearing the active unit
      // must not wipe a selection the shopper is still working on.
      if (!equipment) return;
      if (this.state.equipment?.id === equipment.id) return;

      // No selection write here: the unit is already the active one in the garage, and
      // `getSavedEquipment` falls back to the active unit.
      this.applyEquipment(equipment);
    }

    // One shape for "this is the unit now": the three form fields, the record composed
    // from them, and the display that follows.
    applyEquipment({ make, model, serial = "" }) {
      const equipment = window.garageService?.normalizeEquipment({ make, model, serial });
      if (!equipment) return;

      this.state.make = make;
      this.state.model = model;
      this.state.serial = serial;
      this.state.equipment = equipment;

      this.updateEquipmentDisplay();
      this.setState("has-equipment");
    }

    // A unit the shopper chose here, as opposed to one the garage told us about: it
    // becomes the pending selection and the rest of the page is told.
    selectEquipment(equipment) {
      this.applyEquipment(equipment);
      this.saveEquipment();

      this.dispatchEvent(
        new CustomEvent("ymm:equipment-selected", {
          bubbles: true,
          detail: this.state.equipment,
        }),
      );
    }

    // The selector is rendered into every page by `layout/theme.liquid`, but the form it
    // needs options for is only on screen in the `expanded` state - a shopper with a saved
    // unit, or on mobile before they tap in, never sees it. Loading on demand keeps a POST
    // per page view off the app proxy, and the promise is kept so expanding, collapsing
    // and expanding again costs one request.
    //
    // The memo is only kept for a load that reached an answer. A load that failed, or one
    // whose element went away mid-flight, clears it in `loadData` so the next expand tries
    // again - otherwise one bad response would leave the form dead until a page reload.
    ensureData() {
      this._dataPromise ||= this.loadData();
      return this._dataPromise;
    }

    async loadData() {
      const endpoint = this.dataset.endpoint || window.theme?.ymm?.searchEndpoint;

      // Nothing to point at, or the API asset never arrived: retrying cannot change
      // either, so the memo stands and the form says so once.
      if (!endpoint || !window.ymmApi) {
        this.setDataUnavailable();
        return;
      }

      try {
        const { makes, models } = await window.ymmApi.fetchOptions(endpoint, {
          signal: this._abortController?.signal,
        });
        if (!this.isConnected) {
          // The response outlived the element, so the options were never applied.
          this._dataPromise = null;
          return;
        }
        this.makes = makes;
        this.models = models;
      } catch (error) {
        this._dataPromise = null;
        if (error.name === "AbortError") return;
        console.error("Failed to load YMM data:", error);
        this.setDataUnavailable();
        return;
      }

      // An answer with no options is still an answer - the store has no YMM data - so
      // this one keeps the memo.
      if (!this.makes.length) {
        this.setDataUnavailable();
        return;
      }

      this.setDataAvailable();
      this.renderMakes();
    }

    // Without options there is nothing to pick, so lock the form down and say so
    // instead of offering an empty dropdown.
    setDataUnavailable() {
      const trigger = this.makeDropdown?.querySelector(".js-ymm-dropdown-trigger");
      if (trigger) {
        trigger.disabled = true;
        const valueEl = trigger.querySelector(".js-ymm-dropdown-value");
        const message = this.dataset.unavailableMessage;
        if (valueEl && message) valueEl.textContent = message;
      }
      this.makeDropdown?.classList.add("ymm-dropdown--disabled");

      if (this.submitBtn) this.submitBtn.disabled = true;
    }

    // The inverse of `setDataUnavailable`, for a retry that succeeds after a failed load:
    // the options are in hand but the form is still locked behind the unavailable
    // message, and `renderMakes` only fills the list. The submit button is recomputed
    // rather than enabled, because make and model may still be unpicked.
    setDataAvailable() {
      const trigger = this.makeDropdown?.querySelector(".js-ymm-dropdown-trigger");
      if (trigger) {
        trigger.disabled = false;

        // Only the unavailable message is ours to take back - anything else is either the
        // untouched placeholder or a unit the shopper picked.
        const valueEl = trigger.querySelector(".js-ymm-dropdown-value");
        if (valueEl && valueEl.textContent === this.dataset.unavailableMessage) {
          valueEl.textContent = trigger.dataset.placeholder;
        }
      }
      this.makeDropdown?.classList.remove("ymm-dropdown--disabled");

      this.updateSubmitButton();
    }

    setInitialState() {
      const hasEquipment = this.dataset.hasEquipment === "true";
      const savedEquipment = this.getSavedEquipment();

      if (hasEquipment || savedEquipment) {
        this.state.equipment = savedEquipment;
        this.setState("has-equipment");
        this.updateEquipmentDisplay();
      } else if (this.isDesktopView) {
        this.setState("expanded");
      } else {
        this.setState("collapsed");
      }
    }

    setState(state) {
      this.dataset.state = state;

      if (state === "expanded") this.ensureData();

      const isExpanded = state === "expanded";
      const hasEquipment = state === "has-equipment";
      const isCollapsed = state === "collapsed";

      if (this.toggle) {
        this.toggle.setAttribute("aria-expanded", String(isExpanded || hasEquipment));
      }

      if (this.collapsed) this.collapsed.hidden = !isCollapsed;
      if (this.form) this.form.hidden = !isExpanded;
      if (this.equipment) this.equipment.hidden = !hasEquipment;

      if (hasEquipment) {
        document.body.classList.add("selected-equipment");
      } else {
        document.body.classList.remove("selected-equipment");
      }

      this.updateClearVisibility();
    }

    expand() {
      this.setState("expanded");
    }

    collapse() {
      if (!this.isDesktopView) {
        this.setState("collapsed");
      }
    }

    toggleExpand() {
      const currentState = this.dataset.state;
      if (currentState === "collapsed") {
        this.expand();
      } else if (currentState === "expanded") {
        this.collapse();
      }
    }

    escapeHTML(str) {
      const div = document.createElement("div");
      div.textContent = str;
      return div.innerHTML;
    }

    // Every option needs a stable id: `aria-activedescendant` on the trigger is what tells
    // a screen reader which one the arrow keys have landed on, and it can only point at an
    // id. Without it the list reads as silent - the focus ring is purely visual.
    renderOptions(list, options) {
      if (!list) return;

      list.innerHTML = options
        .map(
          (option, index) => `
        <li
          class="ymm-dropdown__option js-ymm-dropdown-option"
          role="option"
          id="${list.id}-option-${index}"
          data-value="${this.escapeHTML(option.id)}"
        >
          ${this.escapeHTML(option.name)}
        </li>
      `,
        )
        .join("");
    }

    renderMakes() {
      this.renderOptions(this.makeDropdown?.querySelector(".js-ymm-dropdown-list"), this.makes);
    }

    renderModels(makeId) {
      this.renderOptions(this.modelDropdown?.querySelector(".js-ymm-dropdown-list"), this.models[makeId] || []);
    }

    // The visual focus ring and the announced position are the same thing, so they are
    // always set together. Passing no option clears both.
    setActiveOption(list, option) {
      const trigger = list.closest(".js-ymm-dropdown")?.querySelector(".js-ymm-dropdown-trigger");

      list.querySelectorAll(".ymm-dropdown__option--focused").forEach((opt) => {
        opt.classList.remove("ymm-dropdown__option--focused");
      });

      if (!option) {
        trigger?.removeAttribute("aria-activedescendant");
        return;
      }

      option.classList.add("ymm-dropdown__option--focused");
      if (trigger && option.id) trigger.setAttribute("aria-activedescendant", option.id);
      option.scrollIntoView({ block: "nearest" });
    }

    toggleDropdown(dropdown, event) {
      event?.stopPropagation();

      const trigger = dropdown.querySelector(".js-ymm-dropdown-trigger");
      const list = dropdown.querySelector(".js-ymm-dropdown-list");
      const isExpanded = trigger.getAttribute("aria-expanded") === "true";

      this.closeAllDropdowns();

      if (!isExpanded) {
        trigger.setAttribute("aria-expanded", "true");
        list.hidden = false;

        // Open on the current choice rather than nowhere, so the first arrow key moves
        // from what is already selected and a screen reader says where it is.
        this.setActiveOption(list, list.querySelector('.js-ymm-dropdown-option[aria-selected="true"]'));
      }
    }

    closeAllDropdowns() {
      this.querySelectorAll(".js-ymm-dropdown-trigger").forEach((trigger) => {
        trigger.setAttribute("aria-expanded", "false");
        trigger.removeAttribute("aria-activedescendant");
      });
      this.querySelectorAll(".js-ymm-dropdown-list").forEach((list) => {
        list.hidden = true;
      });
      this.querySelectorAll(".ymm-dropdown__option--focused").forEach((opt) => {
        opt.classList.remove("ymm-dropdown__option--focused");
      });
    }

    // Recording a pick is the same act in both dropdowns: show it on the trigger, mark the
    // dropdown active, and move `aria-selected` onto the chosen option. Only the tail -
    // which field the pick unlocks - differs.
    commitChoice(dropdown, option) {
      const choice = { id: option.dataset.value, name: option.textContent.trim() };

      dropdown.querySelector(".js-ymm-dropdown-value").textContent = choice.name;
      dropdown.classList.add("ymm-dropdown--active");
      dropdown.querySelectorAll(".js-ymm-dropdown-option").forEach((opt) => opt.removeAttribute("aria-selected"));
      option.setAttribute("aria-selected", "true");

      this.closeAllDropdowns();

      return choice;
    }

    selectMake(option) {
      const make = this.commitChoice(this.makeDropdown, option);

      this.state.make = make;
      this.state.model = null;
      this.state.serial = "";

      this.enableModelDropdown(make.id);
      this.resetModelDropdown();
      this.resetSerialField({ disable: true });
      // `updateSubmitButton` ends with `updateClearVisibility`.
      this.updateSubmitButton();
    }

    enableModelDropdown(makeId) {
      this.modelDropdown.classList.remove("ymm-dropdown--disabled");
      const trigger = this.modelDropdown.querySelector(".js-ymm-dropdown-trigger");
      trigger.disabled = false;

      this.renderModels(makeId);
    }

    resetModelDropdown() {
      const trigger = this.modelDropdown.querySelector(".js-ymm-dropdown-trigger");
      const valueEl = trigger.querySelector(".js-ymm-dropdown-value");
      const placeholder = trigger.dataset.placeholder;

      valueEl.textContent = placeholder;
      this.modelDropdown.classList.remove("ymm-dropdown--active");
    }

    selectModel(option) {
      this.state.model = this.commitChoice(this.modelDropdown, option);
      this.state.serial = "";

      this.enableSerialField();
      this.updateSubmitButton();
    }

    enableSerialField() {
      this.resetSerialField({ disable: false });
    }

    resetSerialField({ disable = true } = {}) {
      const input = this.serialField?.querySelector(".js-ymm-textfield-input");
      if (input) {
        input.value = "";
        input.disabled = disable;
      }

      this.serialField?.classList.remove("ymm-textfield--pending");
      if (disable) {
        this.serialField?.classList.add("ymm-textfield--disabled");
      } else {
        this.serialField?.classList.remove("ymm-textfield--disabled");
        this.serialField?.classList.add("ymm-textfield--pending");
      }
    }

    handleSerialInput(event) {
      this.state.serial = event.target.value;
      if (this.serialField) {
        if (this.state.serial.trim()) {
          this.serialField.classList.remove("ymm-textfield--pending");
        } else {
          this.serialField.classList.add("ymm-textfield--pending");
        }
      }
      this.updateSubmitButton();
    }

    updateSubmitButton() {
      // Serial is optional: it is not part of the YMM identity until KNEK-244 defines
      // the `model_number` buckets, so make + model is enough to shop.
      const canSubmit = Boolean(this.state.make && this.state.model);
      if (this.submitBtn) this.submitBtn.disabled = !canSubmit;
      this.updateClearVisibility();
    }

    handleSubmit() {
      if (!this.state.make || !this.state.model) return;

      this.selectEquipment({
        make: this.state.make,
        model: this.state.model,
        serial: this.state.serial.trim(),
      });
    }

    updateEquipmentDisplay() {
      if (!this.state.equipment) return;

      const input = this.equipmentField?.querySelector(".js-ymm-textfield-input");
      if (input) {
        input.value = this.state.equipment.name;
      }
    }

    handleSetFromProduct({ make, model, serial }) {
      // Only auto-populate if no equipment is already saved
      if (this.getSavedEquipment()) return;

      const normalMake = make?.trim() || "";
      const normalModel = model?.trim() || "";
      const normalSerial = serial?.trim() || "";

      if (!normalMake || !normalModel) return;

      const slug = (value) => value.toLowerCase().replace(/\s+/g, "-");

      this.selectEquipment({
        make: { id: slug(normalMake), name: normalMake },
        model: { id: slug(normalModel), name: normalModel },
        serial: normalSerial,
      });
    }

    resetState() {
      this.state = { make: null, model: null, serial: "", equipment: null };
    }

    clearEquipment() {
      this.resetState();
      this.clearSavedEquipment();
      this.resetForm();
      this.setState("expanded");

      this.dispatchEvent(
        new CustomEvent("ymm:equipment-cleared", {
          bubbles: true,
        }),
      );
    }

    clearDropdownSelection(dropdown) {
      if (!dropdown) return;

      dropdown.classList.remove("ymm-dropdown--active");
      dropdown
        .querySelectorAll('.js-ymm-dropdown-option[aria-selected="true"]')
        .forEach((option) => option.removeAttribute("aria-selected"));
    }

    resetForm() {
      this.closeAllDropdowns();

      const makeTrigger = this.makeDropdown?.querySelector(".js-ymm-dropdown-trigger");
      const makeValue = makeTrigger?.querySelector(".js-ymm-dropdown-value");
      if (makeValue && !makeTrigger.disabled) {
        makeValue.textContent = makeTrigger.dataset.placeholder;
      }
      this.clearDropdownSelection(this.makeDropdown);

      const modelTrigger = this.modelDropdown?.querySelector(".js-ymm-dropdown-trigger");
      const modelValue = modelTrigger?.querySelector(".js-ymm-dropdown-value");
      if (modelValue) {
        modelValue.textContent = modelTrigger.dataset.placeholder;
      }
      this.clearDropdownSelection(this.modelDropdown);
      this.modelDropdown?.classList.add("ymm-dropdown--disabled");
      if (modelTrigger) modelTrigger.disabled = true;

      this.resetSerialField({ disable: true });

      if (this.submitBtn) this.submitBtn.disabled = true;
      this.updateClearVisibility();
    }

    handleClearSelection() {
      if (!this.state.make && !this.state.model) return;

      this.resetState();
      this.resetForm();
      this.setState("expanded");
    }

    resetFormForNewEntry() {
      this.resetState();
      this.resetForm();
    }

    captureCopy() {
      this.mobileSubtitle = this.subtitle?.textContent?.trim() || "";
      this.mobileModelPlaceholder = this.modelTrigger?.dataset?.placeholder || "";
      this.desktopSubtitle = this.dataset.desktopSubtitle || this.mobileSubtitle;
      this.desktopModelPlaceholder = this.dataset.desktopModelPlaceholder || this.mobileModelPlaceholder;
      this._lastDesktopState = this.isDesktopView;
    }

    applyResponsiveCopy() {
      const isDesktop = this.isDesktopView;

      if (this.subtitle) {
        this.subtitle.textContent = isDesktop ? this.desktopSubtitle : this.mobileSubtitle;
      }

      if (this.modelTrigger) {
        this.modelTrigger.dataset.placeholder = isDesktop ? this.desktopModelPlaceholder : this.mobileModelPlaceholder;

        if (!this.state.model) {
          const valueEl = this.modelTrigger.querySelector(".js-ymm-dropdown-value");
          if (valueEl) valueEl.textContent = this.modelTrigger.dataset.placeholder;
        }
      }

      this._lastDesktopState = isDesktop;
    }

    updateClearVisibility() {
      if (!this.clearSelectionField) return;
      const shouldShow = this.isDesktopView && this.dataset.state === "expanded" && this.state.make && this.state.model;
      this.clearSelectionField.hidden = !shouldShow;
    }

    handleResize() {
      this.clearTimeout(this._resizeTimer);
      this._resizeTimer = this.setTimeout(() => {
        const isDesktop = this.isDesktopView;
        if (this._lastDesktopState !== isDesktop) {
          this.applyResponsiveCopy();
        }
        this.updateClearVisibility();
      }, 100);
    }

    shopEquipment() {
      if (!this.state.equipment) return;

      const url = window.garageService?.buildShopUrl(this.state.equipment);
      if (!url) return;

      this.dispatchEvent(
        new CustomEvent("ymm:shop-equipment", {
          bubbles: true,
          detail: { equipment: this.state.equipment, url },
        }),
      );

      window.location.href = url;
    }

    saveToGarage() {
      if (!this.state.equipment) return;

      // `add` promotes the unit to the active one - which fires garage:active-changed
      // and keeps the garage UI in sync - and retires the pending selection, so there
      // is no separate selection write to make here.
      window.garageService?.add(this.state.equipment);

      this.dispatchEvent(
        new CustomEvent("ymm:save-to-garage", {
          bubbles: true,
          detail: this.state.equipment,
        }),
      );

      this.showSaveTooltip();
    }

    showSaveTooltip() {
      if (!this.saveTooltip) return;

      this.saveTooltip.classList.add("is-visible");

      this.clearTimeout(this._saveTooltipTimer);
      this._saveTooltipTimer = this.setTimeout(() => {
        this.saveTooltip.classList.remove("is-visible");
      }, 2000);
    }

    // The selector no longer owns any storage. GarageService holds both the pending
    // selection and the saved garage, so the header selector, the on-page selector and
    // My Garage can never disagree about the current unit - and a logged-in shopper's
    // selection follows them to another device with the rest of the garage.
    saveEquipment() {
      window.garageService?.setSelection(this.state.equipment);
    }

    getSavedEquipment() {
      return window.garageService?.getCurrent() || null;
    }

    clearSavedEquipment() {
      window.garageService?.clearSelection();
    }

    handleOutsideClick(event) {
      if (!this.contains(event.target)) {
        this.closeAllDropdowns();
      }
    }

    handleKeydown(event) {
      if (event.key === "Escape") {
        this.closeAllDropdowns();
        return;
      }

      const openDropdown = this.querySelector('.js-ymm-dropdown-trigger[aria-expanded="true"]')?.closest(
        ".js-ymm-dropdown",
      );
      if (!openDropdown) return;

      const list = openDropdown.querySelector(".js-ymm-dropdown-list");
      const options = [...list.querySelectorAll(".js-ymm-dropdown-option")];
      if (!options.length) return;

      const focused = list.querySelector(".ymm-dropdown__option--focused");
      let index = focused ? options.indexOf(focused) : -1;

      if (event.key === "ArrowDown") {
        event.preventDefault();
        index = index < options.length - 1 ? index + 1 : 0;
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        index = index > 0 ? index - 1 : options.length - 1;
      } else if (event.key === "Enter" && focused) {
        event.preventDefault();
        focused.click();
        return;
      } else {
        return;
      }

      this.setActiveOption(list, options[index]);
    }
  }

  customElements.define("ymm-selector", YMMSelector);
}
